import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Ticket,
  Kanban,
  BarChart3,
  Bell,
  Users,
  Shield,
  Send,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Zap,
  Target,
  Sparkles,
  ArrowRight,
  Quote,
  Calendar,
} from 'lucide-react';

const NEXT_GREEN = '#7FD02E';
const TG_BLUE = '#229ED9';

// Mock ticket data for the screencast preview - 100% ficticio
const MOCK_TICKETS = [
  { number: 'TKT-2026-014', title: 'Integración pasarela de pagos', priority: 'Alta', status: 'En Progreso', assignee: 'Diego A.', progress: 65, days: 4, statusColor: 'bg-amber-100 text-amber-700', priorityColor: 'bg-amber-100 text-amber-700' },
  { number: 'TKT-2026-013', title: 'Reporte mensual de cumplimiento', priority: 'Crítica', status: 'Asignado', assignee: 'Lucía B.', progress: 20, days: 9, statusColor: 'bg-violet-100 text-violet-700', priorityColor: 'bg-rose-100 text-rose-700' },
  { number: 'TKT-2026-012', title: 'Alta de nuevo proveedor', priority: 'Alta', status: 'Resuelto', assignee: 'Tomás V.', progress: 100, days: null, statusColor: 'bg-emerald-100 text-emerald-700', priorityColor: 'bg-amber-100 text-amber-700' },
  { number: 'TKT-2026-011', title: 'Sincronización con sistema contable', priority: 'Media', status: 'Nuevo', assignee: 'Sin asignar', progress: 0, days: 12, statusColor: 'bg-blue-100 text-blue-700', priorityColor: 'bg-blue-100 text-blue-700' },
];

const Reveal = ({ children, delay = 0, className = '' }) => {
  return (
    <div
      className={`opacity-0 translate-y-3 animate-[fadeUp_700ms_ease-out_forwards] ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export default function PresentacionPage() {
  const [activeTab, setActiveTab] = useState('tickets');

  useEffect(() => {
    document.title = 'NextPlan · Presentación comercial';
    const id = setInterval(() => {
      setActiveTab((prev) => {
        const order = ['tickets', 'kanban', 'telegram'];
        const i = order.indexOf(prev);
        return order[(i + 1) % order.length];
      });
    }, 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-800 overflow-x-hidden" data-testid="presentacion-page">
      {/* Keyframes for reveal animation */}
      <style>{`
        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .55; transform: scale(.92); }
        }
      `}</style>

      {/* NAVBAR */}
      <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_velocity-ops-1/artifacts/3sjrhvi5_Logo%20NextPlan.png"
            alt="NextPlan"
            className="h-8 object-contain"
          />
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
            <a href="#problema" className="hover:text-gray-900">Problema</a>
            <a href="#solucion" className="hover:text-gray-900">Solución</a>
            <a href="#producto" className="hover:text-gray-900">Producto</a>
            <a href="#valor" className="hover:text-gray-900">Valor</a>
          </div>
          <span className="w-px h-6 bg-transparent" />
        </div>
      </nav>

      {/* HERO */}
      <header className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-8">
          <Reveal>
            <Badge className="bg-[#7FD02E]/10 text-[#5BA51E] border-[#7FD02E]/30 px-3 py-1 text-xs uppercase tracking-wider">
              <Sparkles className="w-3 h-3 mr-1.5" />
              Cumplimiento operacional
            </Badge>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-heading text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              Gestiona, prioriza y <span className="text-[#7FD02E]">cumple</span> con cada cliente
              <span className="block text-gray-400 text-3xl lg:text-4xl mt-3 font-normal">
                sin tickets perdidos, sin SLA reventados, sin reuniones eternas.
              </span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
              <b>NextPlan</b> es el centro operacional donde Jefaturas, Solucionadores y Solicitantes coordinan
              tickets, plazos, avances y métricas en tiempo real — con notificaciones automáticas a Telegram
              cuando algo cambia o algo está por vencer.
            </p>
          </Reveal>
          <Reveal delay={480}>
            <div className="flex flex-wrap items-center gap-8 pt-4 text-xs text-gray-500">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#7FD02E]" /> MultiClientes</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#7FD02E]" /> Integración Telegram</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#7FD02E]" /> SLA y KPIs</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#7FD02E]" /> Hora Chile</div>
            </div>
          </Reveal>
        </div>

        {/* Hero device mock */}
        <Reveal delay={500} className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#7FD02E]/30 via-emerald-200/30 to-transparent blur-3xl rounded-full" />
            <div className="relative rounded-3xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
              <div className="h-9 bg-gray-50 border-b border-gray-100 flex items-center gap-1.5 px-4">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                <span className="ml-3 text-[11px] text-gray-400">nextplan.cl/tickets</span>
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Tickets · NovaTech Solutions</span>
                  <span className="text-[#7FD02E] font-semibold">+ Nuevo</span>
                </div>
                {MOCK_TICKETS.slice(0, 3).map((t) => (
                  <div key={t.number} className="border border-gray-100 rounded-xl p-3 hover:border-[#7FD02E]/40 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] font-mono text-[#7FD02E]">{t.number}</div>
                        <div className="text-sm font-medium text-gray-800 truncate">{t.title}</div>
                      </div>
                      <Badge className={`${t.statusColor} text-[10px]`}>{t.status}</Badge>
                    </div>
                    <div className="flex items-center justify-between mt-2 gap-3">
                      <div className="h-1.5 flex-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#7FD02E]" style={{ width: `${t.progress}%` }} />
                      </div>
                      <span className="text-[10px] text-gray-400 w-8 text-right">{t.progress}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </header>

      {/* PROBLEMA */}
      <section id="problema" className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <Badge className="bg-rose-50 text-rose-700 border-rose-200 mb-4">El problema</Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
              Las solicitudes se pierden entre correos, chats y planillas.
            </h2>
            <p className="text-gray-500 mt-4 leading-relaxed">
              Equipos atrapados entre Excel, hilos infinitos de WhatsApp y email. Sin trazabilidad, sin SLA,
              sin saber quién tiene la pelota.
            </p>
          </Reveal>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { icon: AlertTriangle, title: 'Tickets que nadie ve', desc: 'Llegan por mail/Whatsapp y se olvidan hasta que el cliente reclama.' },
              { icon: Clock, title: 'Plazos rebasados', desc: 'Nadie alerta antes de vencer. Los SLA se incumplen sin previo aviso.' },
              { icon: Users, title: 'Equipos descoordinados', desc: 'Reasignaciones manuales, dueño confuso, doble trabajo o cero trabajo.' },
              { icon: BarChart3, title: 'Sin métricas reales', desc: 'Imposible decirle a un cliente "vamos así" con datos confiables.' },
            ].map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <Card className="border-gray-200 bg-white shadow-sm h-full">
                  <CardContent className="p-5 space-y-2">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                      <p.icon className="w-5 h-5 text-rose-600" />
                    </div>
                    <h3 className="font-semibold text-gray-800">{p.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUCIÓN */}
      <section id="solucion" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge className="bg-[#7FD02E]/10 text-[#5BA51E] border-[#7FD02E]/30 mb-4">La solución</Badge>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
            Un único lugar donde todo pasa.
          </h2>
          <p className="text-gray-500 mt-4 leading-relaxed">
            NextPlan une tickets, asignaciones, fechas límite, avances y alertas Telegram en un flujo claro
            para que jefaturas decidan y solucionadores ejecuten.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {[
            { icon: Ticket, color: '#7FD02E', title: 'Ticketing con contexto', desc: 'Categoría, gerencia/área, prioridad, fecha límite, descripción con estilos (negrita, listas, links).' },
            { icon: Send, color: TG_BLUE, title: 'Telegram en tiempo real', desc: 'Cada evento (creación, asignación, comentario, avance, vencimiento) llega al equipo correcto.' },
            { icon: Kanban, color: '#7FD02E', title: 'Kanban drag & drop', desc: 'Mueve tickets entre Nuevo · Asignado · En Progreso · Resuelto sin abrir ninguno.' },
            { icon: TrendingUp, color: '#7FD02E', title: 'Avance con barra %', desc: 'El solucionador desliza, comenta y la jefatura ve el progreso en vivo.' },
            { icon: Bell, color: '#F59E0B', title: 'Alertas 3 días y 0 días', desc: 'Recordatorios automáticos antes de vencer. Nada se atrasa por olvido.' },
            { icon: Building2, color: '#7FD02E', title: 'MultiClientes', desc: 'Una sola instalación. Cada cliente con sus usuarios, áreas, categorías y tickets.' },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 80}>
              <Card className="border-gray-200 bg-white shadow-sm hover:shadow-lg transition-shadow h-full">
                <CardContent className="p-6 space-y-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${f.color}1A` }}>
                    <f.icon className="w-5 h-5" style={{ color: f.color }} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-gray-800">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRODUCTO – Screencast */}
      <section id="producto" className="bg-gradient-to-b from-gray-50 to-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="bg-[#229ED9]/10 text-[#229ED9] border-[#229ED9]/30 mb-4">El producto en vivo</Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
              Así se ve NextPlan trabajando.
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {[
              { id: 'tickets', label: 'Lista de tickets', icon: Ticket },
              { id: 'kanban', label: 'Tablero Kanban', icon: Kanban },
              { id: 'telegram', label: 'Integración Telegram', icon: Send },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeTab === t.id
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`producto-tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="relative rounded-3xl border border-gray-200 bg-white shadow-2xl overflow-hidden min-h-[520px]">
            <div className="h-10 bg-gray-50 border-b border-gray-100 flex items-center gap-1.5 px-4">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <span className="ml-3 text-[11px] text-gray-400">
                {activeTab === 'tickets' && 'nextplan.cl/tickets'}
                {activeTab === 'kanban' && 'nextplan.cl/kanban'}
                {activeTab === 'telegram' && 'Notificaciones · Telegram'}
              </span>
              <span className="ml-auto flex items-center gap-1.5 text-[10px] text-gray-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ animation: 'pulse-dot 1.4s infinite' }} />
                Cliente Demo Andes
              </span>
            </div>

            {/* TICKETS VIEW */}
            {activeTab === 'tickets' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="font-heading text-xl font-bold">Tickets</h3>
                    <p className="text-xs text-gray-400">4 tickets encontrados</p>
                  </div>
                  <Button size="sm" className="bg-[#7FD02E] hover:bg-[#6BB825] text-white">+ Nuevo Ticket</Button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-400 uppercase">
                      <th className="text-left py-2 font-medium">Ticket</th>
                      <th className="text-left py-2 font-medium">Título</th>
                      <th className="text-left py-2 font-medium">Estado</th>
                      <th className="text-left py-2 font-medium">Prioridad</th>
                      <th className="text-left py-2 font-medium">Asignado</th>
                      <th className="text-left py-2 font-medium">Días restantes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_TICKETS.map((t) => (
                      <tr key={t.number} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 font-mono text-[#7FD02E] text-xs">{t.number}</td>
                        <td className="py-3 text-gray-800">{t.title}</td>
                        <td className="py-3"><Badge className={`${t.statusColor} text-xs`}>{t.status}</Badge></td>
                        <td className="py-3"><Badge className={`${t.priorityColor} text-xs`}>{t.priority}</Badge></td>
                        <td className="py-3 text-gray-600 text-xs">{t.assignee}</td>
                        <td className="py-3">
                          {t.days === null
                            ? <span className="text-xs text-gray-400">cerrado</span>
                            : <Badge className={t.days <= 5 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>{t.days} días</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* KANBAN VIEW */}
            {activeTab === 'kanban' && (
              <div className="p-6">
                <h3 className="font-heading text-xl font-bold mb-5">Tablero Kanban · Drag & Drop</h3>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Nuevo', color: 'bg-blue-100 text-blue-700', tickets: [MOCK_TICKETS[3]] },
                    { label: 'Asignado', color: 'bg-violet-100 text-violet-700', tickets: [MOCK_TICKETS[1]] },
                    { label: 'En Progreso', color: 'bg-amber-100 text-amber-700', tickets: [MOCK_TICKETS[0]] },
                    { label: 'Resuelto', color: 'bg-emerald-100 text-emerald-700', tickets: [MOCK_TICKETS[2]] },
                  ].map((col) => (
                    <div key={col.label} className="bg-gray-50 rounded-xl p-3 space-y-2 min-h-[300px]">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={`${col.color} text-xs`}>{col.label}</Badge>
                        <span className="text-xs text-gray-400">({col.tickets.length})</span>
                      </div>
                      {col.tickets.map((t) => (
                        <div key={t.number} className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm hover:border-[#7FD02E]/40 transition-colors cursor-grab">
                          <div className="text-[10px] font-mono text-[#7FD02E]">{t.number}</div>
                          <div className="text-xs font-medium text-gray-800 mt-1 leading-tight">{t.title}</div>
                          <div className="flex items-center gap-1.5 mt-2">
                            <Badge className={`${t.priorityColor} text-[9px] py-0`}>{t.priority}</Badge>
                            <span className="text-[10px] text-gray-400 ml-auto">{t.assignee}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TELEGRAM VIEW */}
            {activeTab === 'telegram' && (
              <div className="p-8 bg-gradient-to-br from-[#229ED9]/5 via-white to-[#7FD02E]/5 min-h-[520px] flex items-center justify-center">
                <div className="w-full max-w-md space-y-3">
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#229ED9]/10 text-[#229ED9] text-xs font-semibold">
                      <Send className="w-3.5 h-3.5" />
                      NextPlan · Bot privado
                    </div>
                  </div>
                  {[
                    { time: '14:02', emoji: '🆕', title: 'Nuevo ticket creado', body: 'TKT-2026-014 · Integración pasarela de pagos\nPrioridad: Alta · Fecha límite: 05/06/2026 17:00' },
                    { time: '14:08', emoji: '👤', title: 'Asignación de ticket', body: 'Nuevo responsable: Diego Aravena\nPor: Lucía Bravo (Jefatura)' },
                    { time: '15:20', emoji: '📈', title: 'Avance reportado', body: 'Progreso: 30% → 65%\n📝 "Integrada autenticación, falta pruebas QA"' },
                    { time: '16:45', emoji: '⏰', title: 'Ticket a 3 días de vencer', body: 'TKT-2026-013 · Reporte mensual de cumplimiento\nEstado: Asignado · Prioridad: Crítica' },
                  ].map((m, i) => (
                    <Reveal key={i} delay={i * 150}>
                      <div className="bg-white rounded-2xl rounded-tl-md p-3 shadow-sm border border-gray-100">
                        <div className="flex items-start gap-2">
                          <span className="text-lg">{m.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-gray-800">{m.title}</div>
                            <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans leading-relaxed mt-0.5">{m.body}</pre>
                          </div>
                          <span className="text-[10px] text-gray-400 shrink-0">{m.time}</span>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VALOR */}
      <section id="valor" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <Badge className="bg-[#7FD02E]/10 text-[#5BA51E] border-[#7FD02E]/30 mb-4">Propuesta de valor</Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
              Menos correos. Más cumplimiento.
            </h2>
            <p className="text-gray-500 mt-4 leading-relaxed">
              Empresas que adoptan NextPlan reducen drásticamente el tiempo en revisar pendientes y aumentan
              el % de tickets cerrados dentro de SLA.
            </p>
          </Reveal>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { metric: '−65%', label: 'Tiempo en reuniones de seguimiento', icon: Clock },
              { metric: '+82%', label: 'Cumplimiento de SLA mensual', icon: Target },
              { metric: '×3', label: 'Velocidad de respuesta inicial', icon: Zap },
              { metric: '100%', label: 'Trazabilidad de cada solicitud', icon: Shield },
            ].map((v, i) => (
              <Reveal key={v.label} delay={i * 120}>
                <Card className="border-gray-200 bg-white shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-heading text-4xl font-bold text-[#7FD02E]">{v.metric}</div>
                        <p className="text-sm text-gray-500 mt-1 leading-tight">{v.label}</p>
                      </div>
                      <v.icon className="w-7 h-7 text-gray-300" />
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ATRIBUTOS */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal>
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-center mb-3">Pensado para equipos reales</h2>
            <p className="text-gray-500 text-center max-w-2xl mx-auto mb-12">
              Cada atributo responde a una necesidad concreta de operaciones, soporte y desarrollo.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { label: 'Roles claros', desc: 'Admin, Jefatura, Solucionador, Solicitante.' },
              { label: 'Editor enriquecido', desc: 'Negrita, listas, links en cada descripción.' },
              { label: 'Reasignación con motivo', desc: 'Historial completo de quién, cuándo y por qué.' },
              { label: 'Avance + comentario', desc: 'Slider 0-100% con bitácora visible para la jefatura.' },
              { label: 'Filtros y búsqueda', desc: 'Por estado, prioridad, categoría, asignado.' },
              { label: 'Configura tu empresa', desc: 'Podrás adaptar el sistema a tu empresa.' },
            ].map((a, i) => (
              <Reveal key={a.label} delay={i * 60}>
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3 h-full">
                  <CheckCircle2 className="w-5 h-5 text-[#7FD02E] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">{a.label}</div>
                    <p className="text-xs text-gray-500 mt-0.5">{a.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
          <Reveal>
            <h2 className="font-heading text-4xl lg:text-6xl font-bold leading-tight">
              ¿Listo para dejar de perseguir tickets?
            </h2>
            <p className="text-gray-400 mt-4 text-lg max-w-2xl mx-auto">
              Implementación rápida e integración en minutos.
            </p>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-gray-100 py-8 text-center text-sm text-gray-400 bg-white">
        © NextPlan {new Date().getFullYear()} · Centro Operacional de Cumplimiento y Productividad
      </footer>
    </div>
  );
}
